import styled, {css, keyframes} from "styled-components";
import {radii} from "theme/tokens";
import {Actions} from "../components/AccountCards";

/**
 * Styles for the Billing sheet's "Unused purchases" card (kitecorp/transferify-ui#3004), externalized per
 * the repo's convention so the component stays markup + behavior. Token-driven throughout (epic #26), so
 * light and dark fall out of the token layer.
 *
 * The row is the owner-approved prototype's: a kind chip, the purchase's name against its amount, what it
 * covers, then the two dates, then the actions. The withdrawal's confirm step opens INSIDE the row, on the
 * `field` surface, rather than in a dialog: the question is about the one row the reader is looking at,
 * and a dialog would take the row, its amount and its dates off the screen while asking about them.
 *
 * TEAL, not gold, for the same reason `home/components/OverLimitNotice.styles` gives: gold is the app's
 * UPGRADE signal, and this card is about money the reader has already spent on a one-off.
 */

export const Purchases = styled.div`
	display: flex;
	flex-direction: column;
`;

export const Purchase = styled.div`
	display: grid;
	grid-template-columns: 36px minmax(0, 1fr);
	column-gap: 12px;
	padding: 14px 0;

	& + & {
		border-top: 1px solid ${({theme}) => theme.tokens.line};
	}

	&:first-child {
		padding-top: 2px;
	}

	&:last-child {
		padding-bottom: 0;
	}

	@media (max-width: 400px) {
		grid-template-columns: 32px minmax(0, 1fr);
		column-gap: 10px;
	}
`;

/** The purchase family at a glance: teal for a paid send. */
export const KindChip = styled.span`
	width: 36px;
	height: 36px;
	border-radius: 10px;
	display: grid;
	place-items: center;
	background: ${({theme}) => theme.tokens.signalTint};
	color: ${({theme}) => theme.tokens.signalInk};

	@media (max-width: 400px) {
		width: 32px;
		height: 32px;
	}
`;

/**
 * `text-align: start` because the sheet centres its cards' prose, which suits a one-sentence card and
 * not a row that starts with a chip: centred lines under a left-hand chip read as detached from it.
 */
export const Main = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
	text-align: start;
`;

export const Head = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 12px;
`;

export const KindLabel = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Amount = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`;

export const Detail = styled.p`
	margin: 0;
	font-size: 13.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The two dates. Tabular numerals so the rows' dates line up under each other. */
export const Meta = styled.p`
	margin: 0;
	font-size: 12.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink3};
	font-variant-numeric: tabular-nums;
`;

export const MetaSeparator = styled.span`
	padding: 0 5px;
`;

export const RowActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 8px 12px;
	margin-top: 10px;

	@media (max-width: 400px) {
		> button:not(:first-child) {
			flex: 1 1 100%;
		}
	}
`;

/**
 * The text-link control the card and the note share for "Use it". 44px tall for the touch target, with
 * no padding so it reads as a link rather than a second button beside the withdrawal's.
 */
export const linkButtonCss = css`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	min-height: 44px;
	padding: 0;
	background: none;
	border: 0;
	cursor: pointer;
	font: inherit;
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.blue};
	text-decoration: underline;
	text-underline-offset: 3px;
	text-decoration-thickness: 1px;

	&:hover {
		color: ${({theme}) => theme.tokens.blued};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const LinkButton = styled.button.attrs({type: "button"})`
	${linkButtonCss};
`;

/** Past the statutory period: the line that says the purchase stays usable, in place of the withdrawal button. */
export const SupportLine = styled.p`
	margin: 10px 0 0;
	padding: 10px 12px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	font-size: 13px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const Confirm = styled.div`
	margin-top: 12px;
	padding: 14px;
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.fieldLine};
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const ConfirmTitle = styled.h3`
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const ConfirmBody = styled.p`
	margin: 0;
	font-size: 13.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const ConfirmActions = styled(Actions)`
	padding-top: 8px;
	flex-wrap: wrap;

	> button {
		min-height: 44px;
	}

	@media (max-width: 400px) {
		> button {
			flex: 1 1 100%;
		}
	}
`;

/** The withdrawal's result: teal for a declaration received, the error pair for a failure. */
export const Result = styled.div<{$tone: "ok" | "error"}>`
	margin-top: 10px;
	padding: 11px 12px;
	border-radius: ${radii.control};
	display: flex;
	gap: 9px;
	align-items: flex-start;
	font-size: 13.5px;
	line-height: 1.5;
	font-weight: 600;
	background: ${({theme, $tone}) => ($tone === "ok" ? theme.tokens.signalTint : theme.tokens.errorTint)};
	color: ${({theme, $tone}) => ($tone === "ok" ? theme.tokens.signalInk : theme.tokens.errorInk)};

	> svg {
		flex: none;
		margin-top: 2px;
	}
`;

const spin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

/** The in-flight mark on the confirm button, in the button's own ink. */
export const Spinner = styled.span`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid currentColor;
	border-right-color: transparent;
	animation: ${spin} 0.8s linear infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;
